import React, { createContext, useEffect, useState } from 'react';
import { auth, firestore } from '../Firebase';

const AuthContextPublic = createContext();

const AuthProviderPublic = ({ children }) => {
  const [publicUser, setPublicUser] = useState(null);
  const [userID, setUserID] = useState(
    () => localStorage.getItem('userID') || ''
  );
  const [userData, setUserData] = useState(null);
  const [userName, setUserName] = useState(
    () => localStorage.getItem('userName') || ''
  );
  const [eventPasscode, setEventPasscode] = useState('');
  const [error, setError] = useState(null);
  const [hasEventPasscode, setHasEventPasscode] = useState(false);

  useEffect(() => {
    auth.onAuthStateChanged((user) => {
      if (user) {
        setPublicUser(user);
        setUserID(user.uid);
      } else {
        setPublicUser(null);
        setUserID(null);
      }
    });
  }, []);

  useEffect(() => {
    // Combine these two listeners into one
    const unsubscribeSettings = firestore.collection('settings').onSnapshot(
      (snapshot) => {
        snapshot.docs.forEach((doc) => {
          if (doc.id === 'customization') {
            setEventPasscode(doc.data().eventPasscode || '');
          } else if (doc.id === 'features') {
            setHasEventPasscode(doc.data().hasEventPasscode || false);
          }
        });
      },
      (error) => {
        console.error('Error listening to settings changes:', error);
      }
    );

    return () => unsubscribeSettings();
  }, []);

  // Only set up this listener if userID exists
  useEffect(() => {
    if (!userID) return () => {};

    const unsubscribe = firestore
      .collection('viewers')
      .where('userID', '==', userID)
      .onSnapshot((snapshot) => {
        const firestoreItems = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUserData(firestoreItems);
      });

    return () => unsubscribe();
  }, [userID]);

  const publicLogin = async (userName, password, isRegistered = false) => {
    if (isRegistered || !hasEventPasscode || password === eventPasscode) {
      try {
        const userCredential = await auth.signInAnonymously();
        await userCredential.user.getIdToken(true);

        const newUserID = userCredential.user.uid;
        setPublicUser(userCredential.user);
        setUserID(newUserID);
        setUserName(userName);
        localStorage.setItem('userID', newUserID);
        localStorage.setItem('userName', userName);
        setError(null);
        
        // Return the user object so we can use the uid immediately
        return userCredential.user;
      } catch (error) {
        setError(error.message);
        throw error; // Re-throw to handle in component
      }
    } else {
      setError('Incorrect passcode');
      throw new Error('Incorrect passcode');
    }
  };

  const registerUser = async (userData) => {
    try {
      const docRef = firestore.collection('users').doc();
      await docRef.set({
        ...userData,
        userID: docRef.id,
        createdAt: new Date(),
      });
      setError(null);
      return docRef;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const checkUserExists = async (email) => {
    try {
      // First try exact match
      const userQuery = await firestore
        .collection('users')
        .where('email', '==', email)
        .limit(1)
        .get();

      if (!userQuery.empty) {
        const userData = userQuery.docs[0].data();
        return { exists: true, user: userData, ref: userQuery.docs[0].ref };
      }

      // Fallback: Fetch all users and check case-insensitively
      // This is needed because emails are stored with mixed casing
      const allUsersQuery = await firestore.collection('users').get();
      
      const foundDoc = allUsersQuery.docs.find((doc) => {
        const docEmail = doc.data().email;
        return docEmail && docEmail.toLowerCase() === email.toLowerCase();
      });

      if (foundDoc) {
        return { exists: true, user: foundDoc.data(), ref: foundDoc.ref };
      }

      return { exists: false };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const verifyPasscode = (inputPasscode, forceCheck = false) => {
     if ((hasEventPasscode || forceCheck) && inputPasscode !== eventPasscode) {
         return false;
     }
     return true;
  };

  const loginRegisteredUser = async (userData) => {
       // We pass true for isRegistered to bypass passcode check
       await publicLogin(userData.userName, '', true);
  };

  const logoutPublic = () => {
    auth.signOut().then(() => {
      setPublicUser(null);
      setUserName('');
      setUserID(null);
      localStorage.removeItem('userName');
      localStorage.removeItem('userID');
      setError(null);
    });
  };

  const clearError = () => setError(null);

  return (
    <AuthContextPublic.Provider
      value={{
        userPublic: publicUser,
        userID: userID,
        userName: userName,
        userData: userData,
        publicLogin,
        registerUser,
        checkUserExists,
        loginRegisteredUser,
        verifyPasscode,
        logoutPublic,
        error,
        clearError,
      }}
    >
      {children}
    </AuthContextPublic.Provider>
  );
};

export { AuthProviderPublic, AuthContextPublic };
